import {
  IScheduleHandler,
  ScheduleContext,
  ScheduleEvaluationResult,
} from "../types/index";
import {
  Playlist,
  ScheduleType,
  CrossDayPrayerWindowSchedule,
  isCrossDayPrayerWindowSchedule,
} from "../types";
import { getPrayerLength } from "../utils/prayerTimeUtils";

/**
 * Cross-Day Prayer Window Schedule Handler
 *
 * Handles playlists with ScheduleType.CrossDayPrayerWindow (4)
 * Active between prayer times that span across multiple days.
 *
 * Example: Friday Isha to Monday Fajr (weekend special content)
 */
export class CrossDayPrayerWindowScheduleHandler implements IScheduleHandler {
  public readonly scheduleType = ScheduleType.CrossDayPrayerWindow;

  /**
   * Evaluate if playlist is active based on cross-day prayer window
   */
  evaluate(
    playlist: Playlist,
    context: ScheduleContext
  ): ScheduleEvaluationResult {
    const schedule = playlist.schedule;

    if (!schedule || !isCrossDayPrayerWindowSchedule(schedule)) {
      return {
        isActive: false,
        effectivePriority: playlist.priority,
        reason:
          "Invalid or missing CrossDayPrayerWindow schedule configuration",
      };
    }

    if (!context.prayerTimes) {
      return {
        isActive: false,
        effectivePriority: playlist.priority,
        reason: "Prayer times not available",
      };
    }

    const currentDay = context.currentTime.getDay();

    // Get prayer times
    const startPrayerTime = this.getPrayerTime(
      context.prayerTimes,
      schedule.startPrayer
    );
    const endPrayerTime = this.getPrayerTime(
      context.prayerTimes,
      schedule.endPrayer
    );

    if (!startPrayerTime || !endPrayerTime) {
      return {
        isActive: false,
        effectivePriority: playlist.priority,
        reason: `Could not resolve prayer times for ${schedule.startPrayer} or ${schedule.endPrayer}`,
      };
    }

    // Check if in cross-day window
    const isInWindow = this.isInCrossDayPrayerWindow(
      currentDay,
      context.currentTime,
      schedule,
      startPrayerTime,
      endPrayerTime,
      context
    );

    if (!isInWindow) {
      return {
        isActive: false,
        effectivePriority: playlist.priority,
        reason: `Outside cross-day prayer window (${this.getDayName(
          schedule.startDay
        )} ${schedule.startPrayer} - ${this.getDayName(schedule.endDay)} ${
          schedule.endPrayer
        })`,
      };
    }

    // Compute end time with optional offset
    const endTime = new Date(endPrayerTime);
    if (schedule.secondsBeforeEndPrayer) {
      endTime.setSeconds(
        endTime.getSeconds() - schedule.secondsBeforeEndPrayer
      );
    } else {
      const prayerLength = getPrayerLength(
        schedule.endPrayer,
        context.screenData
      );
      endTime.setSeconds(endTime.getSeconds() + prayerLength);
    }

    return {
      isActive: true,
      effectivePriority: playlist.priority,
      deactivationTime: endTime,
      reason: `Active in cross-day prayer window (${this.getDayName(
        schedule.startDay
      )} ${schedule.startPrayer} - ${this.getDayName(schedule.endDay)} ${
        schedule.endPrayer
      })`,
      nextEvaluationTime: endTime,
    };
  }

  /**
   * Get next state change time
   */
  getNextStateChange(
    playlist: Playlist,
    context: ScheduleContext
  ): Date | null {
    const schedule = playlist.schedule;

    if (
      !schedule ||
      !isCrossDayPrayerWindowSchedule(schedule) ||
      !context.prayerTimes
    ) {
      return null;
    }

    // This would require tomorrow's prayer times for accurate calculation
    // For now, return null (will re-evaluate on next tick)
    return null;
  }

  /**
   * Validate configuration
   */
  validateConfiguration(playlist: Playlist): string[] {
    const errors: string[] = [];
    const schedule = playlist.schedule;

    if (!schedule || !isCrossDayPrayerWindowSchedule(schedule)) {
      errors.push(
        "CrossDayPrayerWindow schedule configuration is missing or invalid"
      );
      return errors;
    }

    const crossDaySchedule = schedule as CrossDayPrayerWindowSchedule;

    // Validate schedule type
    if (crossDaySchedule.type !== ScheduleType.CrossDayPrayerWindow) {
      errors.push(
        `Schedule type mismatch: expected ${ScheduleType.CrossDayPrayerWindow}, got ${crossDaySchedule.type}`
      );
    }

    // Validate days
    if (schedule.startDay < 0 || schedule.startDay > 6) {
      errors.push("Start day must be between 0 (Sunday) and 6 (Saturday)");
    }

    if (schedule.endDay < 0 || schedule.endDay > 6) {
      errors.push("End day must be between 0 (Sunday) and 6 (Saturday)");
    }

    // Validate prayer names
    if (!schedule.startPrayer) {
      errors.push("Start prayer is required");
    }

    if (!schedule.endPrayer) {
      errors.push("End prayer is required");
    }

    // Validate it's actually cross-day
    if (
      schedule.startDay === schedule.endDay &&
      schedule.startPrayer === schedule.endPrayer
    ) {
      errors.push(
        "For same-day same-prayer schedules, use PrayerWindow instead"
      );
    }

    return errors;
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  private isInCrossDayPrayerWindow(
    currentDay: number,
    currentTime: Date,
    schedule: CrossDayPrayerWindowSchedule,
    startPrayerTime: Date,
    endPrayerTime: Date,
    context: ScheduleContext
  ): boolean {
    const { startDay, endDay } = schedule;

    // Apply offsets to window start
    const windowStart = new Date(startPrayerTime);
    if (schedule.secondsAfterStartPrayerEnds) {
      const startPrayerLength = getPrayerLength(
        schedule.startPrayer,
        context.screenData
      );
      windowStart.setSeconds(
        windowStart.getSeconds() + startPrayerLength + schedule.secondsAfterStartPrayerEnds
      );
    } else if (schedule.secondsBeforeStartPrayer) {
      windowStart.setSeconds(
        windowStart.getSeconds() - schedule.secondsBeforeStartPrayer
      );
    }

    // Apply offsets to window end
    const endTime = new Date(endPrayerTime);
    if (schedule.secondsBeforeEndPrayer) {
      endTime.setSeconds(
        endTime.getSeconds() - schedule.secondsBeforeEndPrayer
      );
    } else {
      const prayerLength = getPrayerLength(
        schedule.endPrayer,
        context.screenData
      );
      endTime.setSeconds(endTime.getSeconds() + prayerLength);
    }

    // Same day
    if (startDay === endDay) {
      return (
        currentDay === startDay &&
        currentTime >= windowStart &&
        currentTime <= endTime
      );
    }

    // Start day check
    if (currentDay === startDay && currentTime >= windowStart) {
      return true;
    }

    // End day check
    if (currentDay === endDay && currentTime <= endTime) {
      return true;
    }

    // Days in between
    if (endDay > startDay) {
      return currentDay > startDay && currentDay < endDay;
    } else {
      return currentDay > startDay || currentDay < endDay;
    }
  }

  private getPrayerTime(prayerTimes: any, prayerName: string): Date | null {
    const mapping: Record<string, string> = {
      StartFajr: "BegFajr",
      JamFajr: "JamFajr",
      StartSunrise: "Sunrise",
      StartDhuhr: "BegDhuhr",
      JamDhuhr: "JamDhuhr",
      StartAsr: "BegAsar",
      JamAsar: "JamAsar",
      StartMaghrib: "BegMaghrib",
      JamMaghrib: "JamMaghrib",
      StartEsha: "BegEsha",
      JamEsha: "JamEsha",
      Jummah1: "Jummah1",
      Jummah2: "Jummah2",
      Jummah3: "Jummah3",
      Jummah4: "Jummah4",
    };

    const key = mapping[prayerName];
    if (!key || !prayerTimes[key]) {
      return null;
    }

    const timeStr = prayerTimes[key];
    if (!timeStr || typeof timeStr !== "string") {
      return null;
    }

    // Validate time string format (HH:MM)
    const timeParts = timeStr.split(":");
    if (timeParts.length !== 2) {
      return null;
    }

    const hours = parseInt(timeParts[0], 10);
    const minutes = parseInt(timeParts[1], 10);

    // Validate hours and minutes are valid numbers and in range
    if (
      isNaN(hours) ||
      isNaN(minutes) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59
    ) {
      return null;
    }

    try {
      const today = new Date();
      const prayerDate = new Date(today);
      prayerDate.setHours(hours, minutes, 0, 0);

      // Additional validation: ensure the date was set correctly
      if (
        prayerDate.getHours() !== hours ||
        prayerDate.getMinutes() !== minutes
      ) {
        return null;
      }

      return prayerDate;
    } catch (error) {
      // If Date constructor or setHours fails, return null
      return null;
    }
  }

  private getDayName(day: number): string {
    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    return days[day] || "Unknown";
  }
}
